import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_BASE_URL from '../api';

const LanguageInfo = () => {
  const [languageInfo, setLanguageInfo] = useState({
    speaksSameLanguage: '',
    knownLanguages: [],
    otherLanguage: '',
  });

  const [message, setMessage] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [showOtherLanguageField, setShowOtherLanguageField] = useState(false);

  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_BASE_URL}/api/language-info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => {
        if (response.status === 404) {
          setIsNewUser(true);
          setMessage('No language info found. Please set your language info.');
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to fetch language info');
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          const languageData = data.reduce((acc, item) => {
            if (
              item.question_text ===
              'Do you want to meet a partner that speaks the same language as you?'
            ) {
              acc.speaksSameLanguage = item.answer_text;
            }
            if (item.question_text === 'What languages do you know?') {
              const knownLanguages = item.answer_text
                ? item.answer_text.split(', ')
                : [];
              acc.knownLanguages = knownLanguages;
              if (knownLanguages.includes('Other')) {
                setShowOtherLanguageField(true);
                acc.otherLanguage = knownLanguages[knownLanguages.length - 1]; // The last entry is the custom language
              }
            }
            return acc;
          }, {});
          setLanguageInfo(languageData);
        }
      })
      .catch((error) => {
        console.error('Error fetching language info:', error);
        setMessage('Error fetching language info.');
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = getAuthToken();

    const payload = {
      speaksSameLanguage: languageInfo.speaksSameLanguage,
      knownLanguages: showOtherLanguageField
        ? [
            ...languageInfo.knownLanguages.filter((lang) => lang !== 'Other'),
            languageInfo.otherLanguage,
          ] // Only send the custom language without "Other"
        : languageInfo.knownLanguages,
    };

    const method = isNewUser ? 'POST' : 'PUT';

    try {
      const response = await fetch(`${API_BASE_URL}/api/language-info`, {
        method: method,
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update language info');
      }

      setLanguageInfo(payload); // Set state to the values that were just submitted
      setMessage('Language info updated successfully.');

      setTimeout(() => {
        navigate('/almost-done');
      }, 2000);

      setIsNewUser(false);
    } catch (error) {
      console.error('Error setting language info:', error);
      setMessage('Failed to update language info.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLanguageInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleLanguageSelection = (e) => {
    const { selectedOptions } = e.target;
    const values = Array.from(selectedOptions, (option) => option.value);

    if (values.includes('Other')) {
      setShowOtherLanguageField(true);
    } else {
      setShowOtherLanguageField(false);
      setLanguageInfo((prevState) => ({ ...prevState, otherLanguage: '' }));
    }

    setLanguageInfo((prevState) => ({
      ...prevState,
      knownLanguages: values,
    }));
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0D2329' }}
    >
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div
          className="w-full max-w-md p-8 rounded-lg shadow-md"
          style={{ backgroundColor: '#05664f' }}
        >
          <h1 className="text-4xl font-bold text-white text-center mb-6">
            Language Info
          </h1>
          {message && (
            <p className="text-sm text-red-200 text-center mb-4">{message}</p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-white">
              <label className="block text-sm font-medium">
                Do you want to meet a partner that speaks the same language as
                you?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="speaksSameLanguage"
                    value="Yes"
                    checked={languageInfo.speaksSameLanguage === 'Yes'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="speaksSameLanguage"
                    value="No"
                    checked={languageInfo.speaksSameLanguage === 'No'}
                    onChange={handleChange}
                    className="form-radio"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <div className="text-white">
              <label className="block text-sm font-medium">
                What languages do you know?
              </label>
              <select
                multiple
                name="knownLanguages"
                value={languageInfo.knownLanguages.concat(
                  showOtherLanguageField ? ['Other'] : [],
                )}
                onChange={handleLanguageSelection}
                className="block w-full mt-1 px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="English">English</option>
                <option value="Mandarin Chinese">Mandarin Chinese</option>
                <option value="Hindi">Hindi</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
                <option value="Arabic">Arabic</option>
                <option value="Bengali">Bengali</option>
                <option value="Russian">Russian</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Indonesian">Indonesian</option>
                <option value="Urdu">Urdu</option>
                <option value="Japanese">Japanese</option>
                <option value="German">German</option>
                <option value="Turkish">Turkish</option>
                <option value="Vietnamese">Vietnamese</option>
                <option value="Korean">Korean</option>
                <option value="Italian">Italian</option>
                <option value="Persian (Farsi)">Persian (Farsi)</option>
                <option value="Thai">Thai</option>
                <option value="Swahili">Swahili</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {showOtherLanguageField && (
              <div className="text-white">
                <label className="block text-sm font-medium">
                  Please specify other language:
                  <input
                    type="text"
                    name="otherLanguage"
                    value={languageInfo.otherLanguage}
                    onChange={handleChange}
                    className="mt-1 block w-full px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </label>
              </div>
            )}

            <button
              type="submit"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>

            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-2"
              onClick={() => navigate('/pets-info')} // Navigate back to the previous form
            >
              Previous
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LanguageInfo;
